// Typed COAC API client for the web layer.
//
// `CoacAction` is the single COAC model (the legacy `coac_cases` mailbox
// lifecycle was retired). One query hook feeds every COAC view (the routing
// list, the Ops inbox, the Desk inbox); the mutation hooks dispatch the
// CoacAction lifecycle Actions through the typed `api.coac.*` endpoints. After
// every mutation we invalidate `coacKeys.all` so all three views re-render.

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, staffErrorMessage } from '@/lib/api';
import type {
    CoacAccountElectionView,
    CoacActionRow,
    CoacAffectedPortfolios,
    CoacExposureBreakdown,
    CoacExposureSummary,
    CoacImPortfolio,
    CoacResponsesBreakdown,
    CoacResponsesRow,
    CoacResponsesSummaryRow,
    CoacServiceType,
    CoacBreakdownExplanation,
} from '@septima/contract';
// The exclusion reason is read off the wire row, because the contract package
// does not export the name on its own.
import type { CoacExclusionReason } from '@/components/coac/CoacDecisionField';

export const coacKeys = {
    all: ['coac'] as const,
    actions: () => ['coac', 'actions'] as const,
    exposureSummary: (actionIds: readonly string[]) =>
        ['coac', 'exposure', 'summary', [...actionIds].sort()] as const,
    actionExposure: (id: string) => ['coac', 'exposure', 'action', id] as const,
    accountElections: (id: string) => ['coac', 'account-elections', id] as const,
    actionResponses: (id: string) => ['coac', 'responses', 'action', id] as const,
    affectedPortfolios: (id: string) => ['coac', 'affected-portfolios', id] as const,
    imPortfolios: (id: string, imUserId: string) =>
        ['coac', 'im-portfolios', id, imUserId] as const,
    breakdown: (id: string) => ['coac', 'breakdown', id] as const,
    responses: () => ['coac', 'responses', 'all'] as const,
    instructions: () => ['coac', 'instructions'] as const,
    instruction: (id: string) => ['coac', 'instruction', id] as const,
    auditTrail: (id: string) => ['coac', 'audit-trail', id] as const,
    calendarNotes: () => ['coac', 'calendar-notes'] as const,
    signOffs: (id: string) => ['coac', 'sign-offs', id] as const,
};

// ---------------------------------------------------------------------------
// useCoacActions — the single CoacAction read used by every COAC view.
// ---------------------------------------------------------------------------

/**
 * The query of the corporate actions of the actor. `useCoacActions` reads it,
 * and a data slot of a feature file reads it through `useSlotData`. Both share
 * the one cache entry.
 */
export function coacActionsQuery() {
    return queryOptions({
        queryKey: coacKeys.actions(),
        queryFn: async (): Promise<CoacActionRow[]> => {
            const res = await api.coac.listActions();
            if (res.status !== 200) throw new Error(`COAC list failed: ${res.status}`);
            return res.body.actions;
        },
        refetchInterval: 60_000,
    });
}

export function useCoacActions() {
    return useQuery(coacActionsQuery());
}

// A refusal reason has ONE reader (`staffErrorMessage`). This wrapper only
// carries each call site's own label into it. Reading `body.error` here showed
// the HTTP reason phrase ("Bad Request") and hid the governed refusal, which
// Nest puts in `message`.
function errorMessage(body: unknown, fallback: string): string {
    return staffErrorMessage(body, 0, fallback);
}

// ---------------------------------------------------------------------------
// useCoacExposureSummary — the exposure roll-up (discretionary / advisory /
// execution-only) of each visible corporate action, IM-scoped server-side.
// Returns a Map keyed by the ACTION id (SEP-276): Operations can add portfolios
// to, and remove portfolios from, each action, so two actions on one security
// can differ. Disabled until at least one action id is supplied.
// ---------------------------------------------------------------------------

/** The most action ids that one exposure-summary request carries. */
const EXPOSURE_SUMMARY_BATCH = 500;

export function useCoacExposureSummary(actionIds: readonly string[]): {
    data: Map<string, CoacExposureSummary>;
    isLoading: boolean;
} {
    const unique = Array.from(new Set(actionIds.filter((s) => s.length > 0))).sort();
    const query = useQuery({
        queryKey: coacKeys.exposureSummary(unique),
        enabled: unique.length > 0,
        queryFn: async (): Promise<CoacExposureSummary[]> => {
            // The contract takes at most 500 ids in one request, and the firm
            // can hold more actions than that, so the hook sends batches.
            const batches: string[][] = [];
            for (let i = 0; i < unique.length; i += EXPOSURE_SUMMARY_BATCH) {
                batches.push(unique.slice(i, i + EXPOSURE_SUMMARY_BATCH));
            }
            const results = await Promise.all(
                batches.map(async (actionIds) => {
                    const res = await api.coac.exposureSummary({ body: { actionIds } });
                    if (res.status !== 200)
                        throw new Error(`COAC exposure summary failed: ${res.status}`);
                    return res.body.summaries;
                }),
            );
            return results.flat();
        },
    });
    const data = new Map<string, CoacExposureSummary>();
    for (const s of query.data ?? []) data.set(s.actionId, s);
    return { data, isLoading: query.isLoading };
}

// ---------------------------------------------------------------------------
// useCoacActionExposure — per-client/account holdings breakdown for one
// CoacAction's security (lazy: pass `enabled` so it only fetches when the row
// is expanded). IM-scoped server-side.
// ---------------------------------------------------------------------------

export function useCoacActionExposure(actionId: string, enabled: boolean) {
    return useQuery({
        queryKey: coacKeys.actionExposure(actionId),
        enabled,
        queryFn: async (): Promise<CoacExposureBreakdown> => {
            const res = await api.coac.actionExposure({ params: { id: actionId } });
            if (res.status !== 200) throw new Error(`COAC exposure failed: ${res.status}`);
            return res.body;
        },
    });
}

// ---------------------------------------------------------------------------
// useRouteCoacBulk — Ops routes many selected CoacActions to one chosen IM set
// (the multi-select "route to this IM" surface).
// ---------------------------------------------------------------------------

export function useRouteCoacBulk() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { ids: string[]; imUserIds: string[] }) => {
            const res = await api.coac.routeActionsBulk({ body: input });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Bulk route failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// useRouteCoacToHoldersBulk — Ops fans each selected CoacAction out to every
// desk IM holding its company ("send to all holding IMs").
// ---------------------------------------------------------------------------

export function useRouteCoacToHoldersBulk() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { ids: string[] }) => {
            const res = await api.coac.routeActionsToHoldersBulk({ body: input });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Holders route failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// useConfirmCoacAssignment — the Ops review gate: confirm a proposed assignment
// (optionally correcting the IM set).
// ---------------------------------------------------------------------------

export function useConfirmCoacAssignment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; imUserIds?: string[] }) => {
            const res = await api.coac.confirmActionAssignment({
                params: { id: input.id },
                body: { imUserIds: input.imUserIds },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Confirm failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// useRecallCoac — Ops takes a routed CoacAction back from some or all of its
// assigned IMs (SEP-231). `imUserIds` names the IMs to REMOVE. When the recall
// empties the responsible set, the server returns the action to For Review and
// answers with `returnedToReview: true`.
// ---------------------------------------------------------------------------

export function useRecallCoac() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; imUserIds: string[]; note?: string }) => {
            const res = await api.coac.recallActionFromIms({
                params: { id: input.id },
                body: { imUserIds: input.imUserIds, note: input.note },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Recall failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// useDeleteCoacActions — Ops removes selected Pending CoacActions that an
// upload should not have produced (SEP-231). The server answers with the split:
// how many it deleted, and which ids it refused.
// ---------------------------------------------------------------------------

export function useDeleteCoacActions() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { ids: string[] }) => {
            const res = await api.coac.deleteActionsBulk({ body: input });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Delete failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// useApproveCoacElection — finalise the election (terminal: ElectionApproved).
//
// The server refuses with 400 while any affected portfolio still awaits a
// response (SEP-251 request 4). The Ops Mailbox hides the control in that
// state, so this rejection is the belt behind the braces.
// ---------------------------------------------------------------------------

export function useApproveCoacElection() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; approvalNote?: string }) => {
            const res = await api.coac.approveActionElection({
                params: { id: input.id },
                body: { approvalNote: input.approvalNote },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Approve failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// Per-account elections (WP-E)
// ---------------------------------------------------------------------------

// The per-account elections recorded against one CA (lazy: pass `enabled`).
export function useCoacAccountElections(actionId: string, enabled: boolean) {
    return useQuery({
        queryKey: coacKeys.accountElections(actionId),
        enabled,
        queryFn: async (): Promise<CoacAccountElectionView[]> => {
            const res = await api.coac.listAccountElections({ params: { id: actionId } });
            if (res.status !== 200) throw new Error(`Account elections failed: ${res.status}`);
            return res.body.elections;
        },
    });
}

// Record one account's election (the IM ticks an account + picks a decision).
export function useRecordCoacAccountElection() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            id: string;
            portfolioRef: string;
            clientId: string;
            imUserId?: string | null;
            mandateType?: string | null;
            serviceType: CoacServiceType;
            decision: string;
            // SEP-251. The kind of the answer travels as a code beside the
            // text. `electionOptionCode` names one printed option of the
            // notice, and `exclusionReason` says that the client does not hold
            // the security any more. A free-text answer sends neither.
            electionOptionCode?: string | null;
            exclusionReason?: CoacExclusionReason | null;
            // SEP-276. Optional note beside the option. Leave it out to keep
            // the note that the row holds.
            note?: string | null;
            // SEP-277. Why Operations changes an answer that the portfolio holds.
            overrideReason?: string;
        }) => {
            const res = await api.coac.recordAccountElection({
                params: { id: input.id },
                body: {
                    portfolioRef: input.portfolioRef,
                    clientId: input.clientId,
                    imUserId: input.imUserId ?? undefined,
                    mandateType: input.mandateType ?? undefined,
                    serviceType: input.serviceType,
                    decision: input.decision,
                    electionOptionCode: input.electionOptionCode ?? null,
                    exclusionReason: input.exclusionReason ?? null,
                    note: input.note,
                    overrideReason: input.overrideReason,
                },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Record election failed (${res.status})`));
            return res.body;
        },
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: coacKeys.accountElections(vars.id) });
            void qc.invalidateQueries({ queryKey: coacKeys.actionResponses(vars.id) });
            void qc.invalidateQueries({ queryKey: coacKeys.responses() });
            // The affected-portfolio tree holds the row that this write
            // changed, and the Ops override writes through this same mutation
            // (SEP-251 request 4). Without this line the tree keeps the old
            // decision, and it keeps the "Default applied" marker as well.
            void qc.invalidateQueries({ queryKey: coacKeys.affectedPortfolios(vars.id) });
            // SEP-360. A changed response can make the IM sign-off out of date.
            void qc.invalidateQueries({ queryKey: coacKeys.signOffs(vars.id) });
        },
    });
}

// ---------------------------------------------------------------------------
// CEDAR breakdown (the IM's "Explain this event" affordance).
//
// Two halves: the mutation QUEUES a coac_breakdown AgentJob (202 + jobId) and
// the query reads the latest persisted CoacBreakdown artefact for the action
// (null until CEDAR has produced one). The caller polls the query while a job
// is in flight — see CoacDeskInbox.
// ---------------------------------------------------------------------------

/** The persisted CEDAR breakdown for one CoacAction (null when none exists). */
export interface CoacBreakdownView {
    id: string;
    breakdown: string;
    /** SEP-349. The event and each option explained; null on an older breakdown. */
    explanation: CoacBreakdownExplanation | null;
    generatedAt: string;
}

// Queue a CEDAR breakdown job for a CA. Resolves with the AgentJob id.
export function useGenerateCoacBreakdown() {
    return useMutation({
        mutationFn: async (actionId: string): Promise<string> => {
            const res = await api.coac.actionBreakdown({ params: { id: actionId }, body: {} });
            if (res.status !== 202)
                throw new Error(errorMessage(res.body, `Breakdown failed (${res.status})`));
            return res.body.jobId;
        },
    });
}

// The latest persisted breakdown. `poll` flips on a short refetch interval
// while a queued job is running; otherwise a previously generated breakdown is
// served from a fresh-for-a-few-minutes cache so cards render it on mount.
export function useCoacBreakdown(actionId: string, poll = false) {
    return useQuery({
        queryKey: coacKeys.breakdown(actionId),
        queryFn: async (): Promise<CoacBreakdownView | null> => {
            const res = await api.coac.actionBreakdownLatest({ params: { id: actionId } });
            if (res.status !== 200) throw new Error(`Breakdown fetch failed: ${res.status}`);
            return res.body.breakdown;
        },
        refetchInterval: poll ? 2_500 : false,
        staleTime: poll ? 0 : 5 * 60_000,
    });
}

// Per-IM response breakdown for one CA (lazy: pass `enabled`).
export function useCoacActionResponses(actionId: string, enabled: boolean) {
    return useQuery({
        queryKey: coacKeys.actionResponses(actionId),
        enabled,
        queryFn: async (): Promise<CoacResponsesBreakdown> => {
            const res = await api.coac.actionResponses({ params: { id: actionId } });
            if (res.status !== 200) throw new Error(`Responses failed: ${res.status}`);
            return res.body;
        },
    });
}

// Every portfolio one CA affects, answered or not, grouped IM -> client ->
// portfolio (Ops only, SEP-251).
//
// LAZY ON PURPOSE. Pass `enabled` from the disclosure state of the row. The
// Mailbox is a long card list, and this read costs one exposure join plus one
// election read for each corporate action. A read for every card on mount would
// be one request per row of the whole bucket, and Ops opens one row at a time.
// The result stays in the query cache, so a second open of the same row is
// free, and every COAC mutation invalidates `coacKeys.all` beneath it.
export function useCoacAffectedPortfolios(actionId: string, enabled: boolean) {
    return useQuery({
        queryKey: coacKeys.affectedPortfolios(actionId),
        enabled,
        queryFn: async (): Promise<CoacAffectedPortfolios> => {
            const res = await api.coac.actionAffectedPortfolios({ params: { id: actionId } });
            if (res.status !== 200) throw new Error(`Affected portfolios failed: ${res.status}`);
            return res.body;
        },
    });
}

// Flat per-account election rows across every CA (the Ops Responses tab).
export function useCoacResponses() {
    return useQuery({
        queryKey: coacKeys.responses(),
        queryFn: async (): Promise<CoacResponsesRow[]> => {
            const res = await api.coac.listResponses();
            if (res.status !== 200) throw new Error(`Responses list failed: ${res.status}`);
            return res.body.rows;
        },
        refetchInterval: 15_000,
    });
}

// Per-CA response roll-up (voted/total + responded initials) for the Routing
// list columns. Returns a Map keyed by actionId. Refetches with the list.
export function useCoacResponsesSummary(): {
    data: Map<string, CoacResponsesSummaryRow>;
    isLoading: boolean;
} {
    const query = useQuery({
        queryKey: ['coac', 'responses', 'summary'] as const,
        queryFn: async (): Promise<CoacResponsesSummaryRow[]> => {
            const res = await api.coac.responsesSummary();
            if (res.status !== 200) throw new Error(`Responses summary failed: ${res.status}`);
            return res.body.rows;
        },
        refetchInterval: 15_000,
    });
    const data = new Map<string, CoacResponsesSummaryRow>();
    for (const r of query.data ?? []) data.set(r.actionId, r);
    return { data, isLoading: query.isLoading };
}

// Ops finalises a CA (No-Vote sweep + opsStatus -> Completed; Desk read-only).
// SEP-349. The server refuses it until the reply of the custodian is attached.
export function useCompleteCoacAction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; note?: string }) => {
            const res = await api.coac.completeAction({
                params: { id: input.id },
                body: { note: input.note },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Complete failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// Ops notifications inbox (the Corporate Actions header bell).
// ---------------------------------------------------------------------------

const notificationsKey = ['coac', 'notifications'] as const;

export function useOpsNotifications() {
    return useQuery({
        queryKey: notificationsKey,
        queryFn: async () => {
            const res = await api.coac.listNotifications();
            if (res.status !== 200) throw new Error(`Notifications failed: ${res.status}`);
            return res.body;
        },
        // Poll so a vote landing elsewhere surfaces without a manual refresh.
        refetchInterval: 20_000,
    });
}

export function useMarkNotificationsRead() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id?: string) => {
            const res = await api.coac.markNotificationsRead({ body: { id } });
            if (res.status !== 200) throw new Error(`Mark read failed: ${res.status}`);
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: notificationsKey }),
    });
}

// ---------------------------------------------------------------------------
// SEP-276. Operations corrects the affected set of a corporate action in For
// Review. Each change persists at once, and each mutation invalidates every
// COAC read, so the tree, the proposal and the bucket counts all move.
// ---------------------------------------------------------------------------

// Every portfolio of one IM, with the quantity of the security that the
// platform found. Disabled until Ops picks an IM.
export function useCoacImPortfolios(actionId: string, imUserId: string) {
    return useQuery({
        queryKey: coacKeys.imPortfolios(actionId, imUserId),
        enabled: imUserId.length > 0,
        queryFn: async (): Promise<CoacImPortfolio[]> => {
            const res = await api.coac.actionImPortfolios({
                params: { id: actionId },
                query: { imUserId },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Portfolio list failed (${res.status})`));
            return res.body.portfolios;
        },
    });
}

export function useAddCoacPortfolios() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; imUserId: string; portfolioRefs: string[] }) => {
            const res = await api.coac.addActionPortfolios({
                params: { id: input.id },
                body: { imUserId: input.imUserId, portfolioRefs: input.portfolioRefs },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Add portfolios failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

export function useRemoveCoacPortfolio() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; portfolioRef: string }) => {
            const res = await api.coac.removeActionPortfolio({
                params: { id: input.id },
                body: { portfolioRef: input.portfolioRef },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Remove portfolio failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}

export function useSetCoacPortfolioQuantity() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            id: string;
            portfolioRef: string;
            quantity: number | null;
        }) => {
            const res = await api.coac.setActionPortfolioQuantity({
                params: { id: input.id },
                body: { portfolioRef: input.portfolioRef, quantity: input.quantity },
            });
            if (res.status !== 200)
                throw new Error(errorMessage(res.body, `Save quantity failed (${res.status})`));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: coacKeys.all }),
    });
}
